import React, { useMemo } from 'react'

import { Wrapper } from 'Components/UI'
import { Border, Image, Link, Text } from 'Components/UI/_Legacy'
import { DateTime } from 'luxon'

import { BLOG_POST, ROOT } from 'Constants/Paths'

import { BlogRecordFragment } from 'GraphQL/DatoCMS/TypedDocuments'

import {
  Author,
  Authors,
  AvatarHolder,
  BlogLink,
  Container,
  Content,
  FooterContent,
  ImageHolder,
  MainContent,
} from './styles'

type Props = {
  data?: BlogRecordFragment
}

function Hero({ data }: Props) {
  // setZone keeps the timestamp's own offset so the rendered day is the same
  // in every runtime — the server prerenders in UTC while browsers hydrate in
  // the visitor's zone, and a local-zone format mismatches (React #418).
  const published = useMemo(() => {
    if (!data?.date) return null

    return DateTime.fromISO(data.date, { setZone: true }).toFormat(
      'MMMM dd, yyyy',
    )
  }, [data])

  if (!data) return null

  return (
    <>
      <Border secondary topRight />

      <Container>
        <Wrapper>
          <Content>
            <Link
              aria-label={data.title ?? undefined}
              href={BLOG_POST(data.permalink ?? ROOT)}
              width="100%"
            >
              <ImageHolder>
                {/* The featured post's image is the listing's LCP element;
                    lazy-loading it cost ~2.7 s on throttled mobile. */}
                <Image
                  alt=""
                  image={data?.imageLastPublished || data?.image}
                  priority
                />
              </ImageHolder>
            </Link>

            <MainContent>
              <BlogLink href={BLOG_POST(data.permalink ?? ROOT)}>
                {/* The hero post title is the listing page's only h1 (the
                    hero renders on page 1 only). */}
                <Text
                  as="h1"
                  fontSize={[18, 32]}
                  letterSpacing={0.67}
                  lineHeight={1.38}
                  medium
                >
                  {data?.title}
                </Text>
              </BlogLink>

              {published && (
                <Text as="time" dateTime={data?.date} mt={[16, 24]}>
                  {published}
                </Text>
              )}

              <Text mt={[16, 24]}>{data?.excerpt}</Text>

              <FooterContent mt={[16, 32]}>
                <Authors>
                  <Text medium>Authorship:</Text>

                  <Author>
                    <AvatarHolder>
                      <Image
                        alt=""
                        height={32}
                        layout="intrinsic"
                        lazy
                        src={data?.authorAvatar}
                        width={32}
                      />
                    </AvatarHolder>

                    <Text invertedBody ml={8} nowrap>
                      {data?.author}
                    </Text>
                  </Author>

                  {data?.ideaowner && (
                    <Author>
                      <Text display={['none', 'block']} invertedBody>
                        {' &'}
                      </Text>

                      <AvatarHolder ml="8px">
                        <Image
                          alt=""
                          height={32}
                          image={data?.ideaOwnerAvatar}
                          layout="intrinsic"
                          lazy
                          width={32}
                        />
                      </AvatarHolder>

                      <Text invertedBody ml="8px" nowrap>
                        {data?.ideaowner}
                      </Text>
                    </Author>
                  )}
                </Authors>
              </FooterContent>
            </MainContent>
          </Content>
        </Wrapper>
      </Container>
    </>
  )
}

export default Hero
